const DB_NAME = "bkcrawl";
const DB_VERSION = 1;
const STORE_SAVES = "saves";

// ── Internal record shape stored in IndexedDB ─────────────────────────────────

interface SaveRecord {
  slotId: string;
  data: unknown;
}

// ── DB open ───────────────────────────────────────────────────────────────────

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_SAVES)) {
        db.createObjectStore(STORE_SAVES, { keyPath: "slotId" });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(new Error(`IndexedDB open failed: ${String(request.error?.message)}`));
  });
}

// ── Public API ────────────────────────────────────────────────────────────────

/** Write (or overwrite) a save record under `slotId`. */
export async function putSave(slotId: string, data: unknown): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_SAVES, "readwrite");
    tx.objectStore(STORE_SAVES).put({ slotId, data } satisfies SaveRecord);
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      reject(new Error(`putSave failed: ${String(tx.error?.message)}`));
    };
    tx.onabort = () => {
      db.close();
      reject(new Error("putSave aborted"));
    };
  });
}

/** Read a save record by `slotId`. Returns `null` if not found. */
export async function getSave(slotId: string): Promise<unknown | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_SAVES, "readonly");
    const req = tx.objectStore(STORE_SAVES).get(slotId);
    tx.oncomplete = () => {
      db.close();
      const record = req.result as SaveRecord | undefined;
      resolve(record?.data ?? null);
    };
    tx.onerror = () => {
      db.close();
      reject(new Error(`getSave failed: ${String(tx.error?.message)}`));
    };
  });
}

/** Return all stored slot IDs in insertion order. */
export async function listSaves(): Promise<string[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_SAVES, "readonly");
    const req = tx.objectStore(STORE_SAVES).getAllKeys();
    tx.oncomplete = () => {
      db.close();
      resolve((req.result ?? []) as string[]);
    };
    tx.onerror = () => {
      db.close();
      reject(new Error(`listSaves failed: ${String(tx.error?.message)}`));
    };
  });
}

/** Delete a save record. No-op if the slot does not exist. */
export async function deleteSave(slotId: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_SAVES, "readwrite");
    tx.objectStore(STORE_SAVES).delete(slotId);
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      reject(new Error(`deleteSave failed: ${String(tx.error?.message)}`));
    };
    tx.onabort = () => {
      db.close();
      reject(new Error("deleteSave aborted"));
    };
  });
}
